import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getCountry, getRulesetsForCountry } from "@/data/countries";
import { STATUS } from "@/game/rulesets";
import { TESTIDS } from "@/constants/testIds";
import { ArrowLeft, PlayCircle } from "lucide-react";

const COUNTRY_NARRATIVE = {
  zimbabwe: {
    long:
      "The documented Zimbabwean form (Tsoro, Fuva, Imbwe) is played on four rows. Outer rows begin with two counters per pit; inner rows are empty. Players sow anticlockwise around their own two rows, with relay sowing, and capture the opposing column when finishing in an empty inner pit.",
    sections: [
      { title: "Setup", body: "Four rows, usually eight pits per row. Two counters per outer pit; both inner rows empty. 32 counters in total on an 8-pit board." },
      { title: "Turn", body: "Pick up every counter from one of your pits (with 2 or more), then sow them one per pit anticlockwise around your own two rows only." },
      { title: "Relay", body: "If the final counter lands in a pit that already had counters, pick up everything from that pit and continue. Repeat until the final counter lands in a previously empty pit." },
      { title: "Capture", body: "Capture only when finishing in your own empty inner pit and the opposite enemy inner pit is occupied. Capture that pit, then also capture the enemy outer pit in the same column." },
      { title: "Singletons", body: "Cannot start a move from a 1-counter pit while any pit has 2+. When only singletons remain, a singleton may move only if its next pit is empty." },
      { title: "Win", body: "Capture every counter on the opponent's side. (Most implementations also award the win when the opponent has no legal move.)" },
    ],
  },
  mozambique: { long: "Ntxuva (M’pale) is a Mozambican four-row sowing game closely related to Tsoro. Notably, every pit starts with 2 counters, and a capture removes the column plus two extra opponent pits of the capturer's choice.", sections: [
    { title: "Setup", body: "Two seeds in every pit on a 4×8 (or larger) board." },
    { title: "Capture", body: "Standard column capture PLUS the contents of any two additional enemy pits." },
    { title: "Win", body: "Either taking every enemy seed, or having the larger capture when no legal move remains — agree before play." },
  ]},
  "zambia-malawi": { long: "Nsolo (Zambia, Malawi) and Nchombwa use Tsoro-style sowing on a 4×8 board, with one important twist: after each capture, you also remove an equal number of additional counters from anywhere on the opponent's side.", sections: [
    { title: "Setup", body: "Two seeds per pit, but each player's rightmost inner pit starts empty." },
    { title: "Capture", body: "Take the opposing inner pit, then remove the same number again from anywhere on the opponent's side." },
    { title: "Draw", body: "A repeated board position is treated as a draw." },
  ]},
  "south-africa": { long: "Moruba is a substantially larger Tsoro relative, played on a 4×12 board with four counters in every pit. The winner is the player with the largest capture total.", sections: [
    { title: "Setup", body: "Four rows of twelve pits, four counters each (192 counters total)." },
    { title: "Capture", body: "Sowing into your inner row triggers capture of the opposing inner pit and (if it was occupied) the outer pit behind." },
    { title: "Win", body: "Highest capture total wins, not necessarily total elimination." },
  ]},
  uganda: { long: "Omweso (Mweso) is the Ugandan four-row game. Each player arranges their own 32 counters before starting, and captured counters do not leave play — they are immediately re-sown on the capturer's side.", sections: [
    { title: "Setup", body: "Players choose their own opening arrangement of 32 counters each (across 4×8)." },
    { title: "Capture", body: "Captures both enemy pits opposite the landing inner pit. Captured stones are then re-sown from the pit that began the capturing sequence." },
    { title: "Special", body: "Single-counter pits cannot be selected. From certain left-side pits, a clockwise move is allowed if it produces a capture." },
    { title: "Win", body: "The player who cannot make a legal move loses." },
  ]},
  ghana: { long: "Oware Abapa is the classic two-row mancala from Ghana. It is a different game from Tsoro but recognisable to many West African players.", sections: [
    { title: "Setup", body: "Two rows of six pits, four seeds in every pit (48 total)." },
    { title: "Capture", body: "When the final seed lands in an opponent pit and leaves it containing exactly 2 or 3, capture those seeds; continue backwards through enemy 2s/3s." },
    { title: "Feeding", body: "When possible, you must play a move that gives seeds to an opponent whose row is empty. A 'grand slam' is commonly cancelled in tournament rules." },
    { title: "Win", body: "First to 25 seeds wins; 24-24 is a draw." },
  ]},
  nigeria: { long: "Ayo Ọlọ́pọ́n exists in many family rules. One documented public version uses a 'make four' capture rule.", sections: [
    { title: "Setup", body: "Two rows of six, four seeds per pit." },
    { title: "Capture (documented version)", body: "When sowing finishes in an opponent's pit and that pit now contains exactly four seeds, capture those four." },
    { title: "Feeding", body: "Feed an opponent whose row is empty when possible." },
    { title: "Win", body: "Match endings vary: capture-total, opponent-exhaustion, or consecutive wins. Declare before play." },
  ]},
  bao: { long: "Bao is a four-row East African game with substantially more rules than Tsoro. It uses reserves, a 'house' pit, compulsory captures, two phases, and captured counters re-enter play.", sections: [
    { title: "Setup", body: "Four rows of eight; documented Zanzibar opening uses a house pit and a player reserve." },
    { title: "Capture", body: "Compulsory. Captured counters are immediately re-sown into the capturer's front row, possibly causing further captures." },
    { title: "Win", body: "Clearing the opponent's inner row; or depriving the opponent of a legal move." },
    { title: "Note", body: "Bao should be learned as Bao, not as a Tsoro variant." },
  ]},
};

export function RuleDetail() {
  const { country } = useParams();
  const navigate = useNavigate();
  const meta = getCountry(country);
  const rulesets = getRulesetsForCountry(country);
  const narrative = COUNTRY_NARRATIVE[country];

  if (!meta) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Country not found.</p>
          <Button onClick={() => navigate("/rules")} className="rounded-full mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to library
          </Button>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/rules" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> All countries
        </Link>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-4xl">{meta.flag}</span>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{meta.region}</p>
            <h1 className="font-display text-4xl sm:text-5xl">{meta.name}</h1>
          </div>
        </div>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground/85">
          {narrative?.long || meta.blurb}
        </p>

        {narrative?.sections && (
          <div className="mt-10 space-y-6">
            {narrative.sections.map((s) => (
              <div key={s.title}>
                <h3 className="font-display text-xl">{s.title}</h3>
                <p className="mt-2 text-foreground/80 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Rulesets</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {rulesets.map((r) => (
              <Card key={r.id} className="p-4" data-testid={TESTIDS.rulesetItem(r.id)}>
                <div className="flex items-center justify-between">
                  <p className="font-medium">{r.name}</p>
                  <Badge
                    variant={r.status === STATUS.PLAYABLE ? "default" : "outline"}
                    className="rounded-full text-[10px] uppercase tracking-widest"
                  >
                    {r.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.summary}</p>
                {r.status === STATUS.PLAYABLE && (
                  <Button
                    size="sm"
                    onClick={() => navigate("/play/standard")}
                    className="rounded-full mt-3"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" /> Play this ruleset
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>
      </article>
    </Layout>
  );
}
